import type { ApiResponse } from '@/types/api.types';
import type { Participant, Coach, Manager } from '@/types/entities';
import apiService from './api.service';
import { API_ENDPOINTS } from '@/constants/api.constants';
import { logger } from '@/utils/logger';

export interface User {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
    role: string;
    status: 'active' | 'inactive';
    name?: string;
    clubId?: number;
    // Добавляем поля, которые могут быть в ответе от бэкенда
    middleName?: string;
    telegram?: string;
    dateOfBirth?: string;
    gender?: 'male' | 'female';
    weight?: number;
    beltLevel?: string;
    categoryId?: number;
    qualification?: string;
    experience?: number;
    createdAt?: string;
    updatedAt?: string;
}

export interface Club {
    id: number;
    name: string;
    city: string;
    address: string;
    phone: string;
    email: string;
    status: 'active' | 'inactive';
    createdAt: string;
    updatedAt: string;
}

// Удалены дублирующие интерфейсы Manager

class UserService {
    static async getById(id: number): Promise<User> {
        try {
            const response = await apiService.get<ApiResponse<User>>(
                `${API_ENDPOINTS.USERS.BASE}/${id}`
            );

            if (!response.data || !response.data.data) {
                logger.warn('Invalid response format from getById');
                throw new Error('Invalid response format from server');
            }

            const user = response.data.data;
            // Добавляем вычисляемое поле name
            user.name = `${user.firstName} ${user.lastName}`.trim();

            return user;
        } catch (error) {
            logger.error('Error fetching user by ID:', error);
            throw error;
        }
    }

    static async getCurrentUser(): Promise<User> {
        try {
            // Check if apiService is properly initialized
            if (!apiService) {
                throw new Error('API Service is not initialized');
            }

            // Токены теперь в httpOnly cookies, они отправляются автоматически
            const response = await apiService.get<ApiResponse<User>>(
                '/users/me'
            );

            return response.data.data;
        } catch (error: any) {
            // Не логируем 401 ошибки как ERROR, так как это ожидаемое поведение для неавторизованных пользователей
            if (error?.response?.status !== 401) {
                logger.error('Error fetching current user:', error);
            } else {
                
            }

            throw error;
        }
    }

    static async updateProfile(userData: Partial<User>): Promise<User> {
        try {
            const response = await apiService.put<ApiResponse<User>>(
                '/users/profile',
                userData
            );
            return response.data.data;
        } catch (error) {
            logger.error('Error updating user profile:', error);
            throw error;
        }
    }

    static async uploadAvatar(file: File): Promise<string> {
        try {
            const formData = new FormData();
            formData.append('avatar', file);

            const response = await apiService.post<ApiResponse<{avatarUrl: string}>>(
                '/users/avatar',
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );

            return response.data.data.avatarUrl;
        } catch (error) {
            logger.error('Error uploading avatar:', error);
            throw error;
        }
    }

    static async getManagerClub(managerId: number): Promise<ApiResponse<Club>> {
        try {            
            // First, try to get the user to check if they have a clubId directly
            try {
                const userResponse = await apiService.get<ApiResponse<any>>(
                    `/users/${managerId}`
                );

                if (userResponse.data?.data?.clubId) {
                    const clubId = userResponse.data.data.clubId;

                    try {
                        const clubResponse = await apiService.get<ApiResponse<Club>>(
                            `/clubs/${clubId}`
                        );
                        return clubResponse.data;
                    } catch (clubError) {
                        // Continue to other methods if this fails
                    }
                }
            } catch (userError) {
                // Continue to other methods if this fails
            }

            // Try different endpoints based on user role
            try {
                // First try the manager club endpoint
                const endpoint = API_ENDPOINTS.USERS.MANAGER_CLUB(managerId);

                const response = await apiService.get<ApiResponse<Club>>(
                    endpoint
                );

                return response.data;
            } catch (primaryError) {

                // Try alternative endpoints for regular managers
                // First try club-managers endpoint
                const clubManagerEndpoint = `/club-managers/${managerId}/club`;

                try {
                    const clubManagerResponse = await apiService.get<ApiResponse<Club>>(
                        clubManagerEndpoint
                    );
                    return clubManagerResponse.data;
                } catch (clubManagerError) {

                    // Then try managers endpoint
                    const managersEndpoint = `/managers/${managerId}/club`;

                    try {
                        const alternativeResponse = await apiService.get<ApiResponse<Club>>(
                            managersEndpoint
                        );
                        
                        return alternativeResponse.data;
                    } catch (managerEndpointError) {
                        // Continue to fallback
                    }
                }
            }

            // If all endpoints failed, create a fallback response
            // Create a fallback club object that matches the Club interface
            const fallbackClub: Club = {
                id: 0,
                name: 'Unknown Club',
                city: '',
                address: '',
                phone: '',
                email: '',
                status: 'active',
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString()
            };

            return {
                status: 'success' as const,
                data: fallbackClub
            };
        } catch (error) {
            logger.error('Error fetching manager club:', error);

            // Return a fallback response instead of throwing
            // Create a fallback club object that matches the Club interface
            const fallbackClub: Club = {
                id: 0,
                name: 'Unknown Club',
                city: '',
                address: '',
                phone: '',
                email: '',
                status: 'active',
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString()
            };

            return {
                status: 'success' as const,
                data: fallbackClub
            };
        }
    }

    static async getClubById(clubId: number): Promise<ApiResponse<Club>> {
        try {
            const response = await apiService.get<ApiResponse<Club>>(
                `${API_ENDPOINTS.CLUBS.GET(clubId)}`
            );
            return response.data;
        } catch (error) {
            logger.error('Error fetching club by id:', error);
            throw error;
        }
    }

    static async getClubParticipants(clubId: number): Promise<ApiResponse<any>> {
        try {
            if (!clubId) {
                throw new Error('Club ID is required to fetch participants');
            }

            const endpoint = API_ENDPOINTS.CLUBS.PARTICIPANTS.BASE(clubId);

            // Try with different API endpoints if the first one fails
            try {
                const response = await apiService.get(endpoint);

                // Проверяем формат ответа
                if (!response.data) {
                    throw new Error('Invalid response format from server: data is missing');
                }


                return response.data as ApiResponse<any>;
            } catch (primaryError) {
                // Try the alternative endpoint
                const alternativeEndpoint = `/clubs/${clubId}/members`;

                const alternativeResponse = await apiService.get(alternativeEndpoint);

                if (!alternativeResponse.data) {
                    logger.error('Alternative response data is missing');
                    throw primaryError; // Throw the original error if the alternative also fails
                }

                return alternativeResponse.data as ApiResponse<any>;
            }
        } catch (error) {
            // Логируем 429 ошибки как WARN, остальные как ERROR
            if ((error as any)?.response?.status === 429) {
                logger.warn('Rate limit exceeded while fetching club participants:', {
                    clubId,
                    retryAfter: (error as any)?.response?.data?.retryAfter
                });
            } else {
                logger.error('Error fetching club participants:', error);
            }
            throw error;
        }
    }

    static async getAllUsers(params?: { role?: string; status?: string; page?: number; limit?: number }): Promise<ApiResponse<{ rows: User[]; count: number }>> {
        try {
            // Формируем параметры запроса
            const queryParams = new URLSearchParams();

            if (params?.role) {
                queryParams.append('role', params.role);
            }

            if (params?.status) {
                queryParams.append('status', params.status);
            }

            if (params?.page) {
                queryParams.append('page', params.page.toString());
            }

            if (params?.limit) {
                queryParams.append('limit', params.limit.toString());
            }

            const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';

            const response = await apiService.get<ApiResponse<{ rows: User[]; count: number }>>(
                `${API_ENDPOINTS.USERS.BASE}${queryString}`
            );

            // Проверяем формат ответа
            if (!response.data || !response.data.data) {
                logger.warn('Invalid response format from getAllUsers:', response);
                throw new Error('Invalid response format from server');
            }

            return response.data;
        } catch (error) {
            logger.error('Error fetching all users:', error);
            throw error;
        }
    }

    static async getManagers(): Promise<ApiResponse<Manager[]>> {
        try {
            const response = await apiService.get<ApiResponse<Manager[]>>(
                API_ENDPOINTS.USERS.MANAGERS
            );
            return response.data;
        } catch (error) {
            logger.error('Error fetching managers:', error);
            throw error;
        }
    }

    static async getParticipants(): Promise<ApiResponse<Participant[]>> {
        try {
            const response = await apiService.get<ApiResponse<Participant[]>>(
                API_ENDPOINTS.USERS.PARTICIPANTS
            );
            return response.data;
        } catch (error) {
            logger.error('Error fetching participants:', error);
            throw error;
        }
    }

    static async findParticipantsByName(params: {
        firstName: string;
        lastName: string;
        middleName?: string;
    }): Promise<ApiResponse<Participant[]>> {
        try {
            const response = await apiService.get<ApiResponse<Participant[]>>(
                API_ENDPOINTS.PARTICIPANTS.SEARCH,
                { params }
            );
            return response.data;
        } catch (error) {
            logger.error('Error searching participants:', error);
            throw error;
        }
    }

    static async addParticipantToClub(participantId: number, clubId: number): Promise<ApiResponse<void>> {
        try {
            const response = await apiService.post<ApiResponse<void>>(
                `${API_ENDPOINTS.CLUBS.BASE}/${clubId}/participants/${participantId}`
            );
            return response.data;
        } catch (error) {
            logger.error('Error adding participant to club:', error);
            throw error;
        }
    }

    static async createParticipant(participantData: Partial<Participant>): Promise<ApiResponse<Participant>> {
        try {
            const response = await apiService.post<ApiResponse<Participant>>(
                API_ENDPOINTS.PARTICIPANTS.CREATE,
                participantData
            );
            return response.data;
        } catch (error) {
            logger.error('Error creating participant:', error);
            throw error;
        }
    }

    static async updateParticipant(id: number, data: Partial<Participant>): Promise<ApiResponse<Participant>> {
        try {
            // Исправляем путь к API - используем PARTICIPANTS вместо USERS.PARTICIPANTS
            const response = await apiService.put<ApiResponse<Participant>>(
                `${API_ENDPOINTS.PARTICIPANTS.UPDATE(id)}`,
                data
            );
            return response.data;
        } catch (error) {
            logger.error('Error updating participant:', error);
            throw error;
        }
    }

    static async getClubCoaches(clubId: number): Promise<ApiResponse<Coach[]>> {
        try {
            if (!clubId) {
                throw new Error('Club ID is required to fetch coaches');
            }

            // Токены теперь в httpOnly cookies, они отправляются автоматически
            const response = await apiService.get<ApiResponse<Coach[]>>(
                API_ENDPOINTS.CLUBS.COACHES.BASE(clubId)
            );

            // Проверяем формат ответа
            if (!response.data) {
                logger.warn('Invalid response format from getClubCoaches: data is missing');
                throw new Error('Invalid response format from server: data is missing');
            }

            // Если данные приходят в формате { success: true, data: [...] }
            // преобразуем их в формат { success: true, data: { data: [...] } }
            if (Array.isArray(response.data.data)) {
               const originalData = response.data;
                return {
                    status: 'success' as const,
                    data: originalData.data
                };
            }

            return response.data;
        } catch (error) {
            logger.error('Error fetching club coaches:', error);
            throw error;
        }
    }

    static async removeClubCoach(coachId: number): Promise<ApiResponse<any>> {
        try {
            if (!coachId) {
                throw new Error('Coach ID is required to remove coach');
            }

            // Токены теперь в httpOnly cookies, они отправляются автоматически
            const response = await apiService.delete<ApiResponse<any>>(
                API_ENDPOINTS.CLUBS.COACHES.REMOVE(coachId)
            );

            return response.data;
        } catch (error) {
            logger.error('Error removing club coach:', error);
            throw error;
        }
    }

    static async removeClubParticipant(id: number): Promise<ApiResponse<void>> {
        try {
            const response = await apiService.delete<ApiResponse<void>>(
                API_ENDPOINTS.CLUBS.PARTICIPANTS.REMOVE(id)
            );
            return response.data;
        } catch (error) {
            logger.error('Error removing club participant:', error);
            throw error;
        }
    }
}

export default UserService;
